import { EventType, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { ConfigProvider, Spin } from "antd";
import "bootstrap/dist/css/bootstrap.min.css";
import { CircleCheckBig, TriangleAlert, UserPlus } from "lucide-react";
import { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import AuthenticateEmail from "./components/candidate-feedback/AuthenticateEmail";
import AuthenticateEmailCandidateForm from "./components/candidates/AuthenticateEmailCandidateForm";
import SsoLogin from "./components/login/SsoLogin";
import "./css/main.scss";
import "./index.css";
import "react-quill/dist/quill.snow.css";
import { store } from "./redux-store/store";
import { msalConfig } from "./utils/msalConfig";
import TitleUpdater from "./hooks/useTitle";
import {AuthProvider} from "./auth/AuthProvider";
import {PermissionProvider} from "./auth/PermissionProvider";

const root = ReactDOM.createRoot(document.getElementById("root"));
const msalInstance = new PublicClientApplication(msalConfig);
await msalInstance.initialize();
if (
  !msalInstance.getActiveAccount() &&
  msalInstance.getAllAccounts().length > 0
) {
  msalInstance.setActiveAccount(msalInstance.getActiveAccount()[0]);
}

msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
    const account = event.payload.account;
    msalInstance.setActiveAccount(account);
  }
});
const App = lazy(() => import("./App"));

root.render(
  <ConfigProvider theme={{ token: { fontFamily: "'Segoe UI', sans-serif" } }}>
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <PermissionProvider>
          <RouterProvider
            router={createBrowserRouter([
              {
                path: "/login",
                element: (
                  <Suspense
                    fallback={
                      <Spin style={{ minHeight: "100vh" }} size="large">
                        <div className="appLoader" />
                      </Spin>
                    }
                  >
                    <TitleUpdater>
                      <SsoLogin />
                    </TitleUpdater>
                  </Suspense>
                ),
              },
              {
                path: "/candidatefeedback/:c_id",
                element: (
                  <Suspense
                    fallback={
                      <Spin style={{ minHeight: "100vh" }} size="large">
                        <div className="appLoader" />
                      </Spin>
                    }
                  >
                    <TitleUpdater>
                      <Provider store={store}>
                        <AuthenticateEmail />
                      </Provider>
                    </TitleUpdater>
                  </Suspense>
                ),
              },
              {
                path: "/candidate-form/:c_id",
                element: (
                  <Suspense
                    fallback={
                      <Spin style={{ minHeight: "100vh" }} size="large">
                        <div className="appLoader" />
                      </Spin>
                    }
                  >
                    <TitleUpdater>
                      <Provider store={store}>
                        <AuthenticateEmailCandidateForm />
                        <ToastContainer
                          position="bottom-right"
                          theme="colored"
                          autoClose={2000}
                          icon={({ type }) => {
                            switch (type) {
                              case "error":
                                return <TriangleAlert />;
                              case "info":
                                return <CircleCheckBig />;
                              case "success":
                                return <UserPlus />;
                              case "warning":
                                return (
                                  <TriangleAlert className="stroke-yellow-500" />
                                );
                              default:
                                return null;
                            }
                          }}
                        />
                      </Provider>
                    </TitleUpdater>
                  </Suspense>
                ),
              },
              {
                path: "*",
                element: (
                  <Suspense
                    fallback={
                      <Spin style={{ minHeight: "100vh" }} size="large">
                        <div className="appLoader" />
                      </Spin>
                    }
                  >
                    <TitleUpdater>
                      <App />
                    </TitleUpdater>
                  </Suspense>
                ),
              },
            ])}
          ></RouterProvider>
        </PermissionProvider>
      </AuthProvider>
    </MsalProvider>
  </ConfigProvider>
);
